// Material React Table with default settings and language detection
import {
  jiraConfigAtom,
  orgUsersAtom,
  reactTableFiltersAtom,
  reactTableSortingAtom,
  selectedDomainAtom,
  todosDataAtom,
  userAtom,
} from '@/global-store';
import { JiraIssueExport } from '@/pages/report/MyGoals/components/jira/IssueExport/JiraIssueExport';
import { scrollBarStylesOnWhite } from '@assets/theme/base/scrollbarStyles.jsx';
import StringAvatar from '@components/StringAvatar.jsx';
import { avatarColors } from '@helpers/constants';
import { getInitials } from '@helpers/functions';
import { useNotification } from '@hooks/useNotification.jsx';
import {
  useFetchJiraProjects,
  usePostTodo,
  useRemoveTodo,
  useUpdateTodos,
} from '@http/queries.js';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { todoCategoryProps } from '@pages/report/MyGoals/components/utils';
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconChevronDown,
  IconColumns,
  IconDownload,
  IconExternalLink,
  IconFilter,
  IconFilterOff,
  IconLink,
  IconRefresh,
  IconStarFilled,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { atom, useAtom } from 'jotai';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_TableHeadCellFilterContainer,
} from 'material-react-table';
import { MRT_Localization_DE } from 'material-react-table/locales/de';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import { MRT_Localization_IT } from 'material-react-table/locales/it';
import { MRT_Localization_NL } from 'material-react-table/locales/nl';
import { MRT_Localization_PL } from 'material-react-table/locales/pl';
import { MRT_Localization_PT } from 'material-react-table/locales/pt';
import { MRT_Localization_DA } from 'material-react-table/locales/da';
import { MRT_Localization_SV } from 'material-react-table/locales/sv';
import { MRT_Localization_FI } from 'material-react-table/locales/fi';
import { MRT_Localization_TR } from 'material-react-table/locales/tr';
import { MRT_Localization_NO } from 'material-react-table/locales/no';
import PropTypes from 'prop-types';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { JiraIcon } from './customIcons';
import DynamicDialog from './DynamicDialog';

const locales = {
  de: MRT_Localization_DE,
  en: MRT_Localization_EN,
  es: MRT_Localization_ES,
  fr: MRT_Localization_FR,
  it: MRT_Localization_IT,
  nl: MRT_Localization_NL,
  pt: MRT_Localization_PT,
  pl: MRT_Localization_PL,
  da: MRT_Localization_DA,
  sv: MRT_Localization_SV,
  fi: MRT_Localization_FI,
  tr: MRT_Localization_TR,
  no: MRT_Localization_NO,
};

const tableTextFieldProps = () => {
  return {
    variant: 'outlined',
    margin: 'dense',
    size: 'small',
    sx: {
      textAlign: 'left',
      justifyContent: 'flex-start',
      minWidth: '94px',
      width: '100%',
      maxWidth: '160px',

      '& .MuiSelect-outlined': {
        px: '10px !important',
        textAlign: 'left',
        alignItems: 'flex-start',

        '& .MuiBox-root': {
          textAlign: 'left',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',

          '& span': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        },
      },

      '& .MuiOutlinedInput-root': {
        pr: '10px !important',
      },
    },
  };
};

const todoRowProcessingAtom = atom(null);

function OrgUsersMenu({
  open,
  anchorEl,
  handleClose,
  handleAssignee,
  handleRemove,
  orgUsers,
  todoId,
  assignee,
}) {
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);
  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      id='organisation users menu'
      onClick={handleClose}
      onClose={handleClose}
      open={open}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            maxHeight: '400px',
            overflow: 'hidden',
            overflowY: 'auto',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 28,
              height: 28,
              ml: -0.5,
              mr: 1,
            },
            ...scrollBarStylesOnWhite,
          },
        },
      }}
      sx={{ pt: 1 }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
    >
      <MenuItem onClick={() => handleRemove(todoId)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar>
            <IconX />
          </Avatar>

          <Box>{t('removeFromMyGoals')}</Box>
        </Box>
      </MenuItem>
      {assignee ? (
        <MenuItem
          disabled={true}
          sx={{
            mt: 1,
            cursor: 'no-drop',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StringAvatar
              bgColor={
                assignee?.id === user.id
                  ? avatarColors.me
                  : avatarColors[assignee.orgAdmin]
              }
              small
              text={`${assignee.surname + ' ' || ''}${assignee.name || ''}`}
            />

            <Box
              sx={{
                fontWeight: 600,
              }}
            >
              {`${assignee.surname + ' ' || ''}${assignee.name || ''}`}
            </Box>
          </Box>
          <Chip label={t('assignee')} />
        </MenuItem>
      ) : null}
      <ListSubheader disableSticky sx={{ mb: 0.5, background: '#fafafa' }}>
        {t('assignTo')}
      </ListSubheader>
      <MenuItem
        disabled={!assignee}
        onClick={() => handleAssignee(todoId, null)}
        sx={{ cursor: !assignee ? 'no-drop' : 'pointer' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar>
            <IconUser />
          </Avatar>

          <Box>{t('unassigned')}</Box>
        </Box>
      </MenuItem>
      {assignee?.id !== user.id ? (
        <MenuItem onClick={() => handleAssignee(todoId, user.id)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StringAvatar
              bgColor={avatarColors.me}
              small
              text={user.fullName}
            />

            <Box
              sx={{
                fontWeight: 600,
              }}
            >
              {user.fullName}
            </Box>
          </Box>
        </MenuItem>
      ) : null}
      {orgUsers?.map((orgUser) => {
        const fullName = `${orgUser.surname + ' ' || ''}${orgUser.name || ''}`;

        return user.id === orgUser.id || orgUser.id === assignee?.id ? null : (
          <MenuItem
            key={orgUser.id}
            onClick={() => handleAssignee(todoId, orgUser.id)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <StringAvatar
                bgColor={avatarColors[orgUser.orgAdmin]}
                small
                text={fullName}
              />

              <Box
                sx={{
                  fontWeight: 400,
                }}
              >
                {fullName}
              </Box>
            </Box>
          </MenuItem>
        );
      })}
    </Menu>
  );
}

function TodoAssigneeSelector({
  todoId,
  orgUsers,
  todoAssignee,
  handleUpdateAssignee,
  handleRemoveBookmark,
}) {
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleAssigneeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { fullName, initials } = useMemo(() => {
    const fullName = `${todoAssignee?.surname + ' ' || ''}${todoAssignee?.name || ''}`;
    return { fullName, initials: getInitials(fullName) };
  }, [todoAssignee]);

  return (
    <>
      <Tooltip title={`${t('assignTo')}...`}>
        <IconButton
          aria-label={`${t('assignTo')} ${initials}`}
          aria-controls={open ? 'organisation users menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleAssigneeClick}
        >
          <Badge
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              <Avatar
                style={{
                  width: 16,
                  height: 16,
                  border: `2px solid white`,
                  background: '#f6bf4a',
                }}
              >
                <IconChevronDown />
              </Avatar>
            }
          >
            {todoAssignee ? (
              <StringAvatar
                avatarSize={28}
                bgColor={
                  todoAssignee.id === user.id
                    ? avatarColors.me
                    : avatarColors[todoAssignee.orgAdmin]
                }
                small
                text={fullName}
              />
            ) : (
              <Avatar style={{ width: 28, height: 28 }}>
                <IconUser />
              </Avatar>
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <OrgUsersMenu
        anchorEl={anchorEl}
        assignee={todoAssignee}
        handleAssignee={handleUpdateAssignee}
        handleClose={handleMenuClose}
        handleRemove={handleRemoveBookmark}
        open={open}
        orgUsers={orgUsers}
        todoId={todoId}
      />
    </>
  );
}

function TodoJiraSelector({
  todoId,
  todo,
  jiraConfig,
  handleRemoveBookmark,
  exportTodoToJira,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleAssigneeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRemove = () => {
    handleRemoveBookmark(todoId);
  };

  const handleExport = () => {
    exportTodoToJira(todoId, todo);
  };

  const openInJira = () => {
    window.open(`${jiraConfig?.domain}/browse/${todo?.jira?.key}`, '_blank');
  };

  return (
    <>
      <Tooltip title={`${t('options')}...`}>
        <IconButton
          aria-controls={open ? 'organisation users menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleAssigneeClick}
        >
          <Badge
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              <Avatar
                style={{
                  width: 16,
                  height: 16,
                  border: `2px solid white`,
                  background: '#f6bf4a',
                }}
              >
                <IconChevronDown />
              </Avatar>
            }
          >
            <Avatar style={{ width: 28, height: 28 }}>
              <JiraIcon grayscale={!todo.jira} />
            </Avatar>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        id='organisation users menu'
        onClick={handleMenuClose}
        onClose={handleMenuClose}
        open={open}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              maxHeight: '400px',
              overflow: 'hidden',
              overflowY: 'auto',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 28,
                height: 28,
                ml: -0.5,
                mr: 1,
              },
              ...scrollBarStylesOnWhite,
            },
          },
        }}
        sx={{ pt: 1 }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <MenuItem onClick={handleRemove}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar>
              <IconX />
            </Avatar>

            <Box>{t('removeFromMyGoals')}</Box>
          </Box>
        </MenuItem>
        {!todo.jira ? (
          <MenuItem onClick={handleExport} disabled={!jiraConfig?.isValid}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Avatar>
                <IconLink />
              </Avatar>

              <Box>{t('exportToJira')}</Box>
            </Box>
          </MenuItem>
        ) : (
          <MenuItem onClick={openInJira} disabled={!jiraConfig?.isValid}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Avatar>
                <IconExternalLink />
              </Avatar>

              <Box>{t('opneInJira')}</Box>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

function TodoCell({
  cellData,
  rowId,
  bookmarksFilterIdKey,
  bookmarksCategory,
  handleRemoveTodo,
  handlePostTodo,
  handleExportDialogOpen,
}) {
  const { t } = useTranslation();
  const notification = useNotification();

  // use an atom to store the rowId of the row that is currently being processed
  const [todoRowProcessing, setTodoRowProcessing] = useAtom(
    todoRowProcessingAtom,
  );
  const [selectedDomain] = useAtom(selectedDomainAtom);
  const [orgUsers] = useAtom(orgUsersAtom);
  const [user] = useAtom(userAtom);
  const [todosData] = useAtom(todosDataAtom);
  const [jiraConfig] = useAtom(jiraConfigAtom);
  const updateTodosMutation = useUpdateTodos();

  const isExportableToJira =
    jiraConfig && !!todosData?.jira?.jiraProjectsMap?.[selectedDomain];

  function getTodoId() {
    let _todoId = undefined;
    const todos = todosData?.todos;
    if (todos) {
      _todoId = Object.keys(todos).find(
        (key) =>
          todos[key].domain === selectedDomain &&
          todos[key].category === bookmarksCategory &&
          (todos[key].filterId ?? todos[key].props?.filterId) ===
            cellData[bookmarksFilterIdKey],
      );
    }
    return _todoId;
  }

  async function handleUpdateAssignee(todoId, assignee) {
    if (todoRowProcessing) {
      notification.warning(t('requestProcessingWait'));
      return;
    }

    setTodoRowProcessing(rowId);

    const todos = todosData?.todos;

    if (todos && todos[todoId]) {
      const history = todos[todoId]?.history ?? [];

      // type 4: assignee changed
      history.push({
        type: 4,
        activityDate: Date.now(),
        modifier: user.id,
        props: {
          assignedFrom: todos[todoId]?.assignee,
          assignedTo: assignee,
        },
      });
      const todosToUpdate = [{ todoId, assignee, history }];
      try {
        await updateTodosMutation.mutateAsync({
          todosToUpdate,
        });
      } catch (error) {
        console.error('updateTodosMutation', 'ReactTable.jsx', 'error', error);
      }
    } else {
      console.error(
        'updateTodosMutation',
        'ReactTable.jsx',
        'no todosData found',
      );
    }

    setTodoRowProcessing(null);
  }

  function handleRemoveBookmark(todoId) {
    handleBookmarks(cellData, todoId, false, rowId);
  }

  async function exportTodoToJira(todoId, todo) {
    if (todo.jira) return;
    if (todoRowProcessing) {
      notification.warning(t('requestProcessingWait'));
      return;
    }

    const goToPage = todoCategoryProps[todo.category].url(todo);
    const categoryTitle =
      todoCategoryProps[todo.category].label ||
      t(todoCategoryProps[todo.category].i18nLabelKey);
    const extraJiraProps = {
      goToPage,
      title: cellData?.title ?? 'Eye Able - ' + categoryTitle,
      domain: selectedDomain,
    };
    handleExportDialogOpen({
      jira: { ...todo, todoId, ...extraJiraProps },
      json: { ...todo, todoId },
    });
  }

  async function handleBookmarks(tableEntry, todoId, checked, rowId) {
    if (todoRowProcessing) {
      notification.warning(t('requestProcessingWait'));
      return;
    }

    const category = bookmarksCategory;

    setTodoRowProcessing(rowId);

    if (checked) {
      await handlePostTodo(tableEntry, isExportableToJira);
    } else if (todoId) {
      await handleRemoveTodo(todoId, category);
    }

    setTodoRowProcessing(null);
  }

  const todoId = getTodoId();

  const todoAssignee = !todoId
    ? null
    : orgUsers?.find((user) => user.id === todosData?.todos[todoId]?.assignee);

  if (todoRowProcessing === rowId) return <CircularProgress />;

  return todoId ? (
    isExportableToJira ? (
      <TodoJiraSelector
        handleRemoveBookmark={handleRemoveBookmark}
        todoId={todoId}
        todo={todosData?.todos[todoId]}
        jiraConfig={jiraConfig}
        exportTodoToJira={exportTodoToJira}
      />
    ) : orgUsers?.length > 1 ? (
      <TodoAssigneeSelector
        handleRemoveBookmark={handleRemoveBookmark}
        handleUpdateAssignee={handleUpdateAssignee}
        orgUsers={orgUsers}
        todoAssignee={todoAssignee}
        todoId={todoId}
      />
    ) : (
      <Tooltip title={t('removeFromMyGoals')}>
        <IconButton
          onClick={() => handleBookmarks(cellData, todoId, false, rowId)}
        >
          <Avatar
            style={{
              width: 30,
              height: 30,
              color: '#000',
              background: '#ffd050',
            }}
          >
            <IconStarFilled color='#ffd050' />
          </Avatar>
        </IconButton>
      </Tooltip>
    )
  ) : (
    <Tooltip title={t('addToMyGoals')}>
      <IconButton
        disabled={isExportableToJira && !jiraConfig.isValid}
        sx={{
          opacity: isExportableToJira && !jiraConfig.isValid ? 0.5 : 1,
        }}
        onClick={() => handleBookmarks(cellData, todoId, true, rowId)}
      >
        <Avatar style={{ width: 30, height: 30 }}>
          <IconStarFilled />
        </Avatar>
      </IconButton>
    </Tooltip>
  );
}

// * @param {boolean | string} props.enableRowVirtualization - Whether to enable row virtualization, 'auto' will enable auto mode.

/**
 * ReactTable component to render a table with given configurations.
 *
 * @param {Object} props - ReactTable props.
 * @param {Array} props.columns - An array of column objects.
 * @param {Array} props.data - An array of row data.
 * @param {string} props.id - Optional id for the table. Only usfull if the same table is used on multiple pages or pages with dynamic url segments.
 * @param {Object} [props.initialState] - Initial state of the table.
 * @param {string} [props.exportFilename='table-export'] - Filename for the exported table data.
 * @param {Array<Object>} [props.exportFileHeaders=null] - Custom header for file export
 * @param {Array<string>} [props.ignoredDataKeysOnExport=null] - Keys to be ignored during data export.
 * @param {Array<string>} [props.onlyDataKeysOnExport=null] - Only these keys will be included during data export. Will take precedence over `ignoredDataKeysOnExport` if both are defined.
 * @param {boolean} props.enableBookmarks - Indicates whether to enable bookmarks.
 * @param {boolean} props.enableColumnFilters - Indicates whether to enable column filters using filter button.
 * @param {boolean} props.enableTopToolbar - Indicates whether to enable the top bar.
 * @param {boolean} props.enableSorting - Indicates whether to enable soring.
 * @param {Object} props.bookmarksCategory - Bookmarks category.
 * @param {string} props.bookmarksFilterIdKey - Bookmarks filter id key.
 * @param {Array<string>} props.bookmarksIncludedKeys - Bookmarks included keys.
 * @param {function} props.renderDetailPanel - Render function for the detail panel.
 * @param {boolean} props.refetchDataBtn - Hide/show refetch button in bottom toolbar.
 * @param {function} props.handleRefetchData - Function to refetch table data
 * @param {function} props.customExport - Use Custom Export function instead the defined onExport
 * @param {function} props.expandFirstRow - Wether to expand or not the first row
 * @param {boolean} props.forceShowHideColumnsButton - Force display of the Show/Hide all columns action.
 * @param {Object} props.props - Rest of the props.
 */
const ReactTable = forwardRef(function ReactTableRef(props, ref) {
  const {
    columns = [],
    data = [],
    id = null,
    initialState = {},
    defaultSortBy = null,
    disableExport = false,
    exportFilename = 'table-export',
    exportFileHeaders = null,
    ignoredDataKeysOnExport = null,
    onlyDataKeysOnExport = null,
    enableBookmarks = false,
    enableColumnFilters = true,
    enableTopToolbar = true,
    enableSorting = true,
    bookmarksCategory = '',
    bookmarksFilterIdKey = 'filterId',
    bookmarksIncludedKeys = [],
    renderDetailPanel = null,
    maxHeight = 'calc(100vh - 220px)',
    refetchDataBtn = false,
    handleRefetchData = null,
    //enableRowVirtualization = false,
    customExport = false,
    expandFirstRow = true,
    forceShowHideColumnsButton = false,
    ...restProps
  } = props;

  const { t, i18n } = useTranslation();
  const localization = locales[i18n.resolvedLanguage] || locales.en;
  const location = useLocation();
  const jiraIssueExportRef = useRef();
  const notification = useNotification();

  const [selectedDomain] = useAtom(selectedDomainAtom);

  const largeColumns = useMediaQuery('(min-width:1620px)');

  //   const [isFullScreen , setIsFullScreen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [refetchBtnDisabled, setRefreshBtnDisabled] = useState(false);
  const [jiraExportDialogOpen, setJiraExportDialogOpen] = useState({
    open: false,
  });
  const { data: jiraProjects } = useFetchJiraProjects();

  const [filters, setFilters] = useAtom(reactTableFiltersAtom);
  const [sortBy, setSortBy] = useAtom(reactTableSortingAtom);

  const tableId = id
    ? id
    : (
        window.location.pathname +
        columns.length +
        enableBookmarks +
        defaultSortBy +
        bookmarksFilterIdKey
      ).replaceAll(' ', '');

  const sortByKey =
    sortBy?.[tableId] ||
    defaultSortBy ||
    columns[0]?.id ||
    columns[0]?.accessorKey;

  const [desc, setDesc] = useState(
    columns.find((col) => (col.id ?? col.accessorKey) === sortByKey)
      ?.sortDescFirst ?? false,
  );

  // fix reseting filters when filter dialog is open
  const columnFilters = useMemo(() => {
    const _filters = [];

    if (!filters?.[tableId]) return _filters;

    for (const filter of filters[tableId]) {
      if (
        filter.value === undefined ||
        filter.value === null ||
        (Array.isArray(filter.value) &&
          (!filter.value.length ||
            filter.value.some((v) => v === undefined || v === null)))
      )
        continue;
      else _filters.push(filter);
    }

    return _filters;
  }, [filters, tableId]);

  function handleExportDialogOpen(data) {
    setJiraExportDialogOpen({ open: true, data });
  }

  function handleExportDialogClose() {
    setJiraExportDialogOpen({ open: false });
  }

  const handleExportDialogSubmit = async () => {
    const response = await postTodoToJIRA();
    if (response) setJiraExportDialogOpen({ open: false });
  };

  const postTodoToJIRA = async () => {
    if (!jiraIssueExportRef?.current?.handlePost) return false;
    try {
      const jiraResponse = await jiraIssueExportRef?.current?.handlePost();
      if (!jiraResponse) return false;

      const { id, key } = jiraResponse;

      const todoData = jiraExportDialogOpen.data.json;
      let updatedTodo = { jira: { id, key } };
      if (todoData?.todoId)
        updatedTodo = { ...updatedTodo, todoId: todoData?.todoId };
      else updatedTodo = { ...updatedTodo, ...todoData };

      const response = await handleSaveTodoToJSON(updatedTodo);
      if (response) {
        setJiraExportDialogOpen({ open: false });
      }
      notification.success(t('jiraIssuePostSuccess'), 15000);
      return !!response;
    } catch (error) {
      console.error('error_postTodoToJira', error);
      notification.error(t('jiraIssuePostFailed'), 15000);

      return false;
    }
  };

  /* const _enableRowVirtualization = useMemo(() => {
        return data?.length > 500 ?? false;
    }, [data?.length]); */

  const _enableRowVirtualization = false;

  const showHideColumnsButtonVisible =
    forceShowHideColumnsButton || columns?.length > 5;

  const rerenderTable = useCallback(
    function rerenderTableCallback(selector) {
      const currentUrl = window.location.href;

      // check if the elements are rendered
      setTimeout(() => {
        // do not run if user has navigated away from the page
        if (currentUrl !== window.location.href) return;
        if (!data.length) return;
        // check if the table is filtered and there are no visible rows
        if (
          // TODO: add translations support
          document.evaluate(
            '//div[contains(., "Keine Ergebnisse")]',
            document,
            null,
            XPathResult.ANY_TYPE,
            null,
          ) ||
          document.evaluate(
            '//div[contains(., "No records")]',
            document,
            null,
            XPathResult.ANY_TYPE,
            null,
          )
        )
          return;

        const elements = document.querySelectorAll(selector);

        if (!elements?.length) {
          console.info('react table: elements not found', selector);
          setRenderKey((prev) => prev + 1);
          rerenderTable(selector); // recheck again if there was an error
        }
      }, 600);
    },
    [data.length],
  );

  const [todosData] = useAtom(todosDataAtom);
  const [user] = useAtom(userAtom);
  const [jiraConfig] = useAtom(jiraConfigAtom);

  const linkedJiraProject = useMemo(() => {
    if (!jiraConfig || !jiraProjects || !todosData?.jira?.jiraProjectsMap)
      return undefined;

    const jiraProjectKey = todosData?.jira?.jiraProjectsMap?.[selectedDomain];
    return jiraProjects?.values?.find(
      (project) => project.key == jiraProjectKey,
    );
  }, [
    jiraConfig,
    jiraProjects,
    todosData?.jira?.jiraProjectsMap,
    selectedDomain,
  ]);

  const postTodoMutation = usePostTodo();
  const removeTodoMutation = useRemoveTodo();
  const updateTodosMutation = useUpdateTodos();

  const handleSaveTodoToJSON = useCallback(
    async (todo) => {
      let response = undefined;
      try {
        if (todo.todoId)
          response = await updateTodosMutation.mutateAsync({
            todosToUpdate: [{ ...todo }],
          });
        else response = await postTodoMutation.mutateAsync(todo);
        return response;
      } catch (error) {
        console.error('handleSaveTodoToJSON', 'error', error);
      }
    },
    [postTodoMutation, updateTodosMutation],
  );

  const handlePostTodo = useCallback(
    async (tableEntry, isExportableToJira = false) => {
      const category = bookmarksCategory;

      let newTodo;
      newTodo = {
        filterId: tableEntry[bookmarksFilterIdKey],
        props: {},
        category,
        reporter: user.id,
        assignee: user.id,
        resolved: false,
        dateCreated: Date.now(),
        history: [
          {
            type: 1,
            activityDate: Date.now(),
            modifier: user.id,
          },
        ],
      };

      for (const key of bookmarksIncludedKeys) {
        newTodo.props[key] = tableEntry[key];
      }

      if (isExportableToJira && jiraConfig?.alwaysExport === 1) {
        const goToPage = todoCategoryProps[newTodo.category].url(newTodo);
        const categoryTitle =
          todoCategoryProps[newTodo.category].label ||
          t(todoCategoryProps[newTodo.category].i18nLabelKey);
        const extraJiraProps = {
          goToPage,
          title: tableEntry?.title ?? 'Eye Able - ' + categoryTitle,
          domain: selectedDomain,
        };
        handleExportDialogOpen({
          jira: { ...newTodo, ...extraJiraProps },
          json: { ...newTodo },
        });
      } else {
        const response = await handleSaveTodoToJSON(newTodo);
        return response;
      }
    },
    [
      bookmarksCategory,
      bookmarksFilterIdKey,
      bookmarksIncludedKeys,
      handleSaveTodoToJSON,
      jiraConfig?.alwaysExport,
      selectedDomain,
      t,
      user.id,
    ],
  );

  const handleRemoveTodo = useCallback(
    async (todoId, category) => {
      console.log('remove todo', todoId);
      try {
        await removeTodoMutation.mutateAsync(todoId);
      } catch (error) {
        console.error('removeTodoMutation', category, 'error', error);
      }
    },
    [removeTodoMutation],
  );

  function onExport(rows) {
    let columnHeaders = [];
    if (exportFileHeaders) columnHeaders = exportFileHeaders;
    else {
      for (const column of columns) {
        const columnKey = column.accessorKey || column.id;

        if (
          (onlyDataKeysOnExport?.length &&
            onlyDataKeysOnExport.includes(columnKey) &&
            !column.ignoreOnExport) ||
          (ignoredDataKeysOnExport?.length &&
            !ignoredDataKeysOnExport.includes(columnKey) &&
            !column.ignoreOnExport) ||
          (!onlyDataKeysOnExport?.length &&
            !ignoredDataKeysOnExport?.length &&
            !column.ignoreOnExport)
        ) {
          columnHeaders.push({
            key: columnKey,
            displayLabel: column.header,
          });
        }
      }
    }

    let filename = location.pathname.toLowerCase().includes('/report/')
      ? `${exportFilename}__${selectedDomain}`
      : exportFilename;

    filename = `${filename}__${new Date().toISOString().split('T')[0]}`;

    const csvConfig = mkConfig({
      quoteStrings: false,
      fieldSeparator: ';',
      filename,
      decimalSeparator: i18n.resolvedLanguage === 'de' ? ',' : '.',
      useBom: true,
      columnHeaders,
    });

    // Converts your Array<Object> to a CsvOutput string based on the configs
    const flattenRow = (row) => {
      const flattened = {};
      for (const key in row) {
        if (row[key] instanceof Date) {
          // Convert Date objects to ISO string
          flattened[key] = row[key].toISOString();
        } else if (typeof row[key] === 'object' && row[key] !== null) {
          // Convert other objects to JSON strings
          flattened[key] = JSON.stringify(row[key]);
        } else {
          // Keep primitive values as is
          flattened[key] = row[key];
        }
      }
      return flattened;
    };
    let test = rows.map((row) => flattenRow(row.original));
    console.log('test', test);
    console.log('headers', columnHeaders);
    const csv = generateCsv(csvConfig)(
      rows.map((row) => flattenRow(row.original)),
    );

    download(csvConfig)(csv);
  }

  const initialTableState = useMemo(() => {
    return {
      density: largeColumns ? 'comfortable' : 'compact',
      showColumnFilters: false,
      showGlobalFilter: true,
      expanded: { 0: expandFirstRow },
      ...initialState,
      pagination: {
        pageSize: 100,
        pageIndex: 0,
        ...(initialState?.pagination ?? {}),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [largeColumns, initialState]);

  const _paginationEnabled =
    data?.length > (initialTableState?.pagination?.pageSize || 100);

  const _columns = useMemo(() => {
    if (enableBookmarks)
      return [
        {
          size: 56,
          maxSize: 56,
          accessorKey: 'todoId',
          Cell: ({ cell }) => {
            return (
              <TodoCell
                bookmarksCategory={bookmarksCategory}
                bookmarksData={todosData}
                bookmarksFilterIdKey={bookmarksFilterIdKey}
                bookmarksIncludedKeys={bookmarksIncludedKeys}
                cellData={cell.row.original}
                rowId={bookmarksCategory + cell.row.id}
                handleExportDialogOpen={handleExportDialogOpen}
                handleRemoveTodo={handleRemoveTodo}
                handlePostTodo={handlePostTodo}
              />
            );
          },
          header: '',
          enableColumnFilter: false,
          enableColumnSort: false,
          enableColumnActions: false,
          enableColumnDragging: false,
          enableColumnFilterModes: false,
          enableColumnOrdering: false,
          enableHiding: false,
          enableSorting: false,
        },
        ...columns,
      ];
    else return columns;
  }, [
    enableBookmarks,
    columns,
    bookmarksCategory,
    todosData,
    bookmarksFilterIdKey,
    bookmarksIncludedKeys,
    handleRemoveTodo,
    handlePostTodo,
  ]);

  // force rerender on prop changes
  const tableKey = useMemo(() => {
    let _tableKey = largeColumns ? 'large' : 'small';
    _tableKey += _enableRowVirtualization ? 'virtual' : 'nonVirtual';
    _tableKey += data?.length || 0;
    _tableKey += renderDetailPanel ? 'detailPanel' : 'noDetailPanel';
    _tableKey += renderKey;
    _tableKey += i18n.resolvedLanguage;
    _tableKey += sortBy;
    _tableKey += tableId;
    return _tableKey;
  }, [
    i18n.resolvedLanguage,
    largeColumns,
    _enableRowVirtualization,
    data?.length,
    renderDetailPanel,
    renderKey,
    tableId,
    sortBy,
  ]);

  useEffect(() => {
    // rerender table if there are now visible rows
    rerenderTable('tbody td');
  }, [rerenderTable]);

  useEffect(() => {
    if (renderDetailPanel) {
      // force rerender if the expand icon is not rendered
      rerenderTable('svg[data-testid="ExpandMoreIcon"]');
    }
  }, [rerenderTable, renderDetailPanel, enableBookmarks]);

  const refetchDataClicked = () => {
    handleRefetchData();
    setRefreshBtnDisabled(true);
    setTimeout(() => {
      setRefreshBtnDisabled(false);
    }, 10000);
  };

  const onColumnFiltersChange = (filterFn) => {
    const res = filterFn(columnFilters);
    setFilters((prev) => ({ ...prev, [tableId]: res }));
  };

  useImperativeHandle(ref, () => ({
    tableId,
    sortBy,
    setSortBy: (newSortBy) => setSortBy({ ...sortBy, [tableId]: newSortBy }),
    columnFilters,
    setColumnFilters: (newFilters) =>
      setFilters((prev) => ({ ...prev, [tableId]: newFilters })),
  }));

  const uid = useId();

  return (
    <>
      <MaterialReactTable
        onColumnFiltersChange={onColumnFiltersChange}
        enableHiding
        enableGlobalFilterModes
        globalFilterFn='contains'
        enableMultiSort={false}
        icons={{
          ArrowDownwardIcon: (props) => <IconArrowDown {...props} />,
          ViewColumnIcon: (props) => <IconColumns {...props} />,
          SortIcon: (props) => <IconArrowsSort {...props} />,
        }}
        renderToolbarInternalActions={({ table }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {enableSorting ? (
                <TextField
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <IconArrowsSort size={23} strokeWidth={1.7} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  key='sortByField'
                  select
                  fullWidth
                  size='small'
                  label={t('sortBy')}
                  value={sortByKey}
                  onChange={(e) => {
                    setSortBy((prev) => ({
                      ...prev,
                      [tableId]: e.target.value,
                    }));

                    const column = columns.find(
                      (c) => (c.id || c.accessorKey) === e.target.value,
                    );

                    setDesc(column?.sortDescFirst ?? false);
                  }}
                  sx={{ width: '250px' }}
                >
                  {columns.map((column, index) =>
                    column.enableSorting === false ? null : (
                      <MenuItem
                        key={column.accessorKey + column.id + index}
                        value={column.id ?? column.accessorKey}
                      >
                        {column.header}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              ) : null}

              <span id={'filter-status-' + uid} className='sr-only'>
                {table.getState().columnFilters?.length} {t('activeFilters')}
              </span>
              {enableColumnFilters === false ? null : (
                <DynamicDialog
                  key='filterDialog'
                  activatorText={
                    <Badge
                      aria-hidden='true'
                      color='secondary'
                      badgeContent={table.getState().columnFilters?.length}
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -9,
                          top: -6,
                          border: `1px solid #333`,
                          padding: '0 4px',
                        },
                      }}
                    >
                      {t('toFilter')}
                    </Badge>
                  }
                  activatorProps={{
                    'aria-label': t('toFilter'),
                    'aria-describedby': 'filter-status-' + uid,
                    startIcon: <IconFilter />,
                    variant: 'text',
                    sx: { px: 1.5, ml: '0.5rem' },
                  }}
                  title={t('filterTable')}
                  hideConfirmBtn
                  cancelBtnText={t('close')}
                  dialogActionsContent={
                    <Button
                      sx={{
                        ml: 0,
                        mr: 'auto',
                      }}
                      onClick={() => {
                        table.resetGlobalFilter();
                        setFilters((prev) => ({
                          ...prev,
                          [tableId]: [],
                        }));
                      }}
                      variant='text'
                      startIcon={<IconFilterOff />}
                      disabled={
                        !(columnFilters.length || table.getState().globalFilter)
                      }
                    >
                      {t('resetFilters')}
                    </Button>
                  }
                >
                  {table.getLeafHeaders().map((header, index) => {
                    if (!header.column.getCanFilter()) return null;

                    return (
                      <Fragment key={header.id}>
                        <Box
                          id={header.column.columnDef.header + '-label'}
                          sx={[
                            {
                              fontWeight: 500,
                              mb: '-10px',
                            },
                            index === 0
                              ? {
                                  mt: 0.5,
                                }
                              : {
                                  mt: 2.25,
                                },
                          ]}
                        >
                          {t('filterBy')} {header.column.columnDef.header}:
                        </Box>
                        <MRT_TableHeadCellFilterContainer
                          in
                          header={header}
                          table={table}
                        />
                      </Fragment>
                    );
                  })}
                </DynamicDialog>
              )}
              {showHideColumnsButtonVisible ? (
                <MRT_ShowHideColumnsButton table={table} />
              ) : null}
              {/* <Tooltip
                key='fullScreenTooltip'
                PopperProps={{ style: { zIndex: 9999 } }}
                title={t(
                  isFullScreen ? 'deactivateFullScreen' : 'activateFullScreen',
                )}
              >
                <IconButton
                  key='fullScreenButton'
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  sx={[
                    {
                      width: 41,
                      height: 41,
                    },
                    isFullScreen
                      ? {
                          mr: 1.5,
                        }
                      : {
                          mr: 0,
                        },
                  ]}
                >
                  {isFullScreen ? (
                    <IconArrowsMinimize />
                  ) : (
                    <IconArrowsMaximize />
                  )}
                </IconButton>
              </Tooltip> */}
            </Box>
          );
        }}
        columnFilterDisplayMode='custom'
        enableColumnActions={false}
        initialState={initialTableState}
        columns={_columns}
        data={data}
        enablePagination={_paginationEnabled}
        paginationDisplayMode='pages'
        muiPaginationProps={{
          variant: 'outlined',
          showRowsPerPage: false,
        }}
        enableDensityToggle={false}
        enableRowVirtualization={_enableRowVirtualization}
        enableStickyHeader
        enableTableFooter={false}
        enableTopToolbar={enableTopToolbar}
        enableSorting={enableSorting}
        key={tableKey}
        localization={localization}
        // onIsFullScreenChange={() => {
        //   if (isFullScreen) {
        //     document.body.style.overflow = 'auto';
        //   } else document.body.style.overflow = 'hidden';

        //   setIsFullScreen(!isFullScreen);
        // }}
        defaultColumn={{
          muiTableBodyCellProps: ({ column }) => {
            return {
              ...(column?.columnDef?.muiTableBodyCellProps || {}),

              sx: {
                fontWeight: 500,
                whiteSpace: 'normal',
                fontSize: '15px',
                minWidth: '44px',
                maxWidth: {
                  xs: '260px',
                  md: '380px',
                  xxl: '540px',
                },
                textAlign: column?.columnDef?.textAlign ?? 'center',
                '&:first-of-type': {
                  textAlign: column?.columnDef?.textAlign ?? 'left',
                },
                '&[data-pinned="true"]::before': {
                  backgroundColor: 'rgba(0, 0, 0, 0.0) !important',
                },
                ...(column?.columnDef?.muiTableBodyCellProps?.sx || {}),
              },
            };
          },
          muiTableHeadCellProps: ({ column }) => {
            return {
              ...(column?.columnDef?.muiTableHeadCellProps || {}),
              'aria-label': column.columnDef.header,

              onClick: (e) => {
                e.stopPropagation?.();
                if (column.columnDef.enableSorting === false) return;

                //console.log('column clicked', column.columnDef, column);

                if (
                  sortByKey ===
                  (column.columnDef.id ?? column.columnDef.accessorKey)
                ) {
                  setDesc((prev) => !prev);
                } else {
                  setSortBy((prev) => ({
                    ...prev,
                    [tableId]:
                      column.columnDef.id ?? column.columnDef.accessorKey,
                  }));
                  setDesc(column.columnDef.sortDescFirst ?? false);
                }
              },

              sx: {
                minWidth: '44px',

                '& .MuiList-root': {
                  background: '#fafafa !important',
                  outline: '2px solid red',
                },
                '& .Mui-TableHeadCell-Content-Wrapper': {
                  maxWidth: '100%',
                  cursor:
                    !enableSorting || column.columnDef.enableSorting === false
                      ? 'default !important'
                      : 'pointer !important',
                },
                '& .Mui-TableHeadCell-Content-Labels': {
                  '& .MuiBadge-root': {
                    display: 'none', // hides default sort icon & actions
                  },
                  cursor: 'default !important',
                },
                '& .Mui-TableHeadCell-Content': {
                  textAlign: column?.columnDef?.textAlign ?? 'center',
                  justifyContent: column?.columnDef?.textAlign
                    ? column.columnDef.textAlign === 'left'
                      ? 'flex-start'
                      : column.columnDef.textAlign === 'center'
                        ? 'center'
                        : 'flex-end'
                    : 'center',
                  cursor: 'default !important',
                },
                textAlign: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                '&:first-of-type': {
                  '& .Mui-TableHeadCell-Content': {
                    justifyContent: column?.columnDef?.textAlign
                      ? column.columnDef.textAlign === 'left'
                        ? 'flex-start'
                        : column.columnDef.textAlign === 'center'
                          ? 'center'
                          : 'flex-end'
                      : 'flex-start',
                    textAlign: column?.columnDef?.textAlign ?? 'left',
                  },
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                },
                '&[data-pinned="true"]::before': {
                  backgroundColor: 'rgba(0, 0, 0, 0.0) !important',
                },
                ...(column?.columnDef?.muiTableHeadCellProps?.sx || {}),
              },
            };
          },
          muiEditTextFieldProps: tableTextFieldProps,
          muiFilterTextFieldProps: ({ column }) => {
            return {
              ...tableTextFieldProps,
              'aria-label': t('filterBy') + ' ' + column.columnDef.header,
              variant: 'outlined',
              margin: 'normal',
              SelectProps: {
                multiple: true,
                renderValue: (selected) => {
                  const { columnDef } = column;
                  const {
                    wrapFilterLabelInChip = true,
                    filterSelectOptions = [],
                    header,
                  } = columnDef || {};
                  const placeholder = localization.filterByColumn?.replace(
                    '{column}',
                    String(header),
                  );

                  if (!selected?.length) {
                    return <Box sx={{ opacity: 0.5 }}>{placeholder}</Box>;
                  }

                  const optionsMap = new Map(
                    filterSelectOptions.map(({ value, label }) => [
                      value,
                      label,
                    ]),
                  );

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.25,
                      }}
                    >
                      {selected.map((value, index) => {
                        const label = optionsMap.get(value) ?? value;
                        return wrapFilterLabelInChip ? (
                          <Chip key={index} label={label} />
                        ) : (
                          <Fragment key={index}>{label}</Fragment>
                        );
                      })}
                    </Box>
                  );
                },
              },
            };
          },
          Header: ({ column }) => (
            <Tooltip
              PopperProps={{ style: { zIndex: 9999 } }}
              title={
                enableSorting &&
                (column.columnDef.id ?? column.columnDef.accessorKey) ===
                  sortByKey
                  ? column.columnDef.header +
                    ' (' +
                    t(desc ? 'sortedDesc' : 'sortedAsc') +
                    ')'
                  : column.columnDef.header
              }
              placement='top'
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 3],
                      },
                    },
                  ],
                },
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {column.columnDef.header}

                {enableSorting &&
                (column.columnDef.id ?? column.columnDef.accessorKey) ===
                  sortByKey ? (
                  desc ? (
                    <IconArrowDown
                      size={17}
                      style={{
                        marginLeft: '6px',
                      }}
                      strokeWidth={3}
                    />
                  ) : (
                    <IconArrowUp
                      size={17}
                      style={{ marginLeft: '6px' }}
                      strokeWidth={3}
                    />
                  )
                ) : null}
              </span>
            </Tooltip>
          ),
        }}
        renderBottomToolbarCustomActions={({ table }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                pt: 0.15,
                flexGrow: 1,
              }}
            >
              <Button
                onClick={() => {
                  table.resetGlobalFilter();
                  setFilters((prev) => ({
                    ...prev,
                    [tableId]: [],
                  }));
                }}
                size='small'
                startIcon={<IconFilterOff />}
                disabled={
                  !(columnFilters.length || table.getState().globalFilter)
                }
              >
                <span style={{ marginTop: '3px' }}> {t('resetFilters')}</span>
              </Button>
              {!disableExport && (
                <Button
                  disabled={table.getPrePaginationRowModel().rows.length === 0}
                  onClick={() =>
                    customExport
                      ? customExport()
                      : onExport(table.getPrePaginationRowModel().rows)
                  }
                  size='small'
                  startIcon={<IconDownload />}
                >
                  <span style={{ marginTop: '3px' }}>{t('exportAllRows')}</span>
                </Button>
              )}
              {refetchDataBtn && (
                <Button
                  disabled={refetchBtnDisabled || !handleRefetchData}
                  onClick={refetchDataClicked}
                  size='small'
                  startIcon={<IconRefresh />}
                  aria-label={t('refreshData')}
                >
                  {t('refreshData')}
                </Button>
              )}
              <Box
                sx={{
                  fontWeight: 500,
                  ml: 'auto',
                  pr: 1.5,
                }}
              >
                {table.getPrePaginationRowModel().rows.length
                  ? t('tableRowsCounter', {
                      from:
                        1 +
                        table.getState().pagination.pageSize *
                          (table.getState().pagination.pageIndex ?? 0),
                      to: Math.min(
                        table.getPrePaginationRowModel().rows.length,
                        table.getState().pagination.pageSize *
                          ((table.getState().pagination.pageIndex ?? 0) + 1),
                      ),
                      total: table.getPrePaginationRowModel().rows.length,
                    })
                  : t('noRows')}
              </Box>
            </Box>
          );
        }}
        positionExpandColumn='first'
        renderDetailPanel={renderDetailPanel}
        rowVirtualizerOptions={{
          overscan: 25, //adjust the number or rows that are rendered above and below the visible area of the table
          estimateSize: () => 86, //if your rows are taller than normal, try tweaking this value to make scrollbar size more accurate
        }}
        {...restProps}
        muiBottomToolbarProps={{
          elevation: 0, //change the mui box shadow
          ...props?.muiBottomToolbarProps,
          sx: {
            minHeight: '3.25rem',
            backgroundColor: '#fff',
            borderTopRightRadius: '0 !important',
            borderTopLeftRadius: '0 !important',
            border: 'none',
            boxShadow: 'none',
            borderTop: '2px solid #333',

            '& > .MuiBox-root': {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',

              '& div:nth-of-type(2)': {
                marginLeft: 'auto',
              },
            },
            ...props?.muiBottomToolbarProps?.sx,
          },
        }}
        muiDetailPanelProps={{
          sx: {
            width: '100%',
            p: 0,
            bgcolor: '#fff !important',
            '& > div': {
              maxWidth: '100%',
            },
          },
        }}
        muiSearchTextFieldProps={{
          ...tableTextFieldProps,
          label: t('searchTable'),
          placeholder: t('value'),
        }}
        muiTableBodyProps={{
          elevation: 0,
          ...props?.muiTableBodyProps,
          sx: {
            '& .MuiList-root': {
              background: '#fafafa !important',
              outline: '2px solid red',
            },
            background: '#fff !important',
            borderRadius: 0,
            border: 'none',
            boxShadow: 'none',
            '& tr td:first-of-type': {
              whiteSpace: 'normal',
            },
            //stripe the rows, make odd rows a darker color
            [`& tr:nth-of-type(${renderDetailPanel ? '4n-3' : 'odd'})`]: {
              backgroundColor: '#f5f5f5 !important',
              '& td:first-of-type': {
                backgroundColor: '#f5f5f5f1 !important',
              },
            },
            [`& tr:nth-of-type(${renderDetailPanel ? '4n-1' : 'even'})`]: {
              backgroundColor: '#fff !important',
              '& td:first-of-type': {
                backgroundColor: '#fffffff1 !important',
              },
            },
            '& tr:not(.Mui-TableBodyCell-DetailPanel):hover > td': {
              backgroundColor: '#ffff !important',
            },
            '& tr:not(.Mui-TableBodyCell-DetailPanel):hover td:first-of-type': {
              backgroundColor: '#ffff !important',
            },
            ...props?.muiTableBodyProps?.sx,
          },
        }}
        muiTableContainerProps={{
          elevation: 0, //change the mui box shadow
          ...props?.muiTableContainerProps,
          sx: {
            background: '#fff !important',
            borderRadius: 0,
            minHeight: { xs: '320px', md: '420px', xxl: '520px' },
            maxHeight: maxHeight,
            '& .MuiTableSortLabel-icon': {
              display: 'none', // hides sort icons in the header
            },
            ...scrollBarStylesOnWhite,
            ...props?.muiTableContainerProps?.sx,
          },
        }}
        muiTableHeadProps={{
          ...props?.muiTableHeadProps,
          sx: {
            cursor: 'default !important',
            opacity: 1,
            zIndex: 12,
            '& th': {
              backgroundColor: '#fff',
            },

            '& .MuiInputBase-input': {
              minWidth: '24px !important',
            },

            '& .MuiCollapse-wrapper .MuiBox-root': {
              gap: '0.5rem !important',
            },

            '& .MuiCollapse-wrapper .MuiInputBase-input': {
              pr: 0,
            },

            '& .MuiTableCell-head': {
              borderBottom: '2px solid #333',
            },

            ...props?.muiTableHeadProps?.sx,
          },
        }}
        muiTablePaperProps={() => ({
          //   style: {
          //     zIndex: table.getState().isFullScreen ? 1200 : undefined,
          //   },
          elevation: 0, //change the mui box shadow
          ...props?.muiTablePaperProps,
          sx: {
            width: '100%',
            background: '#fff !important',
            ...props?.muiTablePaperProps?.sx,
          },
        })}
        muiTopToolbarProps={{
          elevation: 0, //change the mui box shadow
          ...props?.muiTopToolbarProps,
          sx: {
            mt: /*isFullScreen ? 1.25 : */ 0,
            backgroundColor: '#fff',
            border: 'none',
            boxShadow: 'none',

            '& .MuiCollapse-root': {
              maxWidth: '250px',
            },

            '& > .MuiBox-root': {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',

              '& div:nth-of-type(2)': {
                marginLeft: 'auto',
              },
            },
            ...props?.muiTopToolbarProps?.sx,
          },
        }}
        state={{
          //   isFullScreen,
          ...props?.state,
          sorting: sortByKey
            ? [
                {
                  id: sortByKey,
                  desc,
                },
              ]
            : [],
          columnFilters,
        }}
      />
      {jiraExportDialogOpen?.open ? (
        <DynamicDialog
          hideActivator
          maxWidth='md'
          onCancel={handleExportDialogClose}
          onConfirm={handleExportDialogSubmit}
          overwriteOpen={jiraExportDialogOpen?.open}
          confirmBtnProps={{ color: 'success' }}
          confirmBtnIcon={<IconExternalLink />}
          confirmBtnText={t('export')}
          title={t('exportToJira')}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              my: 2,
            }}
          >
            {/* <Box>{t('exportToJiraPorject')}</Box> */}
            {/* <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <JiraProjectInfo project={jiraProject} />
                </Box> */}
            <JiraIssueExport
              todo={jiraExportDialogOpen.data.jira}
              ref={jiraIssueExportRef}
              jiraProject={linkedJiraProject}
              linkedJiraUserID={todosData?.jira?.jiraUsersMap?.[user.id]}
            />
          </Box>
        </DynamicDialog>
      ) : null}
    </>
  );
});

ReactTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  disableExport: PropTypes.bool,
  exportFilename: PropTypes.string,
  exportFileHeaders: PropTypes.arrayOf(PropTypes.object),
  ignoredDataKeysOnExport: PropTypes.arrayOf(PropTypes.string),
  initialState: PropTypes.object,
  refetchDataBtn: PropTypes.bool,
  handleRefetchData: PropTypes.func,
  forceShowHideColumnsButton: PropTypes.bool,
};

export default ReactTable;
